import { useId, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useDisruptionsList, useOperatedAirports } from '@/lib/disruptions/use-disruptions';
import type { DisruptionCaseRow, DisruptionOverviewRow } from '@/lib/disruptions/types';
import { airportsCsv } from '@/lib/operator-context/airport-scope';
import { InfiniteScrollSentinel } from '@/lib/pagination/infinite-scroll-sentinel';
import { NexaGlyph } from '@/components/nexa-mark/nexa-glyph';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { declareSourceState } from '@/routes/declare/declare-route.state';
import { disruptionEntry } from './disruption-entry';
import { NEXA_FILLED } from './operar/nexa-guide';
import { BoardColumns, PriorityQueue, type QueueVariant } from './priority-queue';
import { boardFlights, type DayGroup, type IBoardKpis, type IBoardNext, type IPaxSum } from './board-model';
import { CountChip, PageHeader } from './board-chrome';
import { FlightWhen } from './flight-when';
import { useOpenBoard } from './use-open-board';
import {
  knownCountries,
  NETWORK_SCOPE,
  parseScope,
  rowAirportCodes,
  scopeToParam,
  type DisruptionsScope,
} from './situation';

// The disruptions LIST (/disruptions) — in the philosophy of «Operar la disrupción»:
// plain, big, what matters. It answers ONE question: what is happening NOW.
//
//   - Disruptions grouped by WHEN (board-model.ts `groupByDay`, the rule is there):
//     «Hoy» first and big, «Ayer» smaller, «Sin movimiento» (OPEN since before
//     yesterday) collapsed, muted, with its count, its unresolved passengers and why.
//     A contained one from before yesterday is not a zombie: it lives in «Pasadas».
//   - ONE number on top: the passengers still unresolved (today + yesterday; plain
//     delays are not «sin resolver»). Not today's disruption count — «Hoy 1» said
//     nothing the «Hoy» group does not (PO 2026-10-29: «un qué?… no aporta nada»).
//     No spend, no case counts, no cause filter — the rows no longer show a cause,
//     and a filter on what you cannot see hides rows silently.
//   - The Network / Region / Airport lens and the header airport scope narrow it; the
//     past ones are one quiet tab away. The network map is gone: it repeated the list
//     in a drawing and took half the screen.
//
// The LOOK is Operar's (PO 2026-10-29: «rediseñarla según los últimos estándares de las
// pantallas nuevas de disrupción»): a header band like Operar's (title, the two tabs,
// the lens, «Declarar» — all in the console's button shape, no pills); Nexa's navy
// card on top with the unresolved passengers and «Lo que sigue»
// — the ONE disruption that needs the operator most (`nextOnBoard`), its row ringed in
// the Nexa cyan below and the card's filled button opening it; then each day group as
// one bordered board with the /express column header, rows on a fixed grid
// (priority-queue.tsx).
//
// On a desktop the open board FITS the screen instead of scrolling it (PO 2026-10-29:
// «que esté autocontenida en el espacio que hay así se muestra la última card»): the
// groups scroll inside themselves and «Sin movimiento» stays in sight at the foot.
// «Ayer» gives up its height first (`shrink-[10000]`), «Hoy» only after it; each keeps
// at least its header and one row. On a phone the page scrolls as before.
//
// Data: `useOpenBoard` (use-open-board.ts) — shared with the operator's home (/dashboard,
// «Resumen»), so its numbers and «Lo que sigue» are this board's; the header band and the
// count chip are shared too (board-chrome.tsx).

const SCOPE_STORAGE_KEY = 'nexa.disruptions.scope';

function readStoredScope(): DisruptionsScope | null {
  try {
    return parseScope(window.localStorage.getItem(SCOPE_STORAGE_KEY));
  } catch {
    return null;
  }
}

function storeScope(scope: DisruptionsScope): void {
  try {
    const param = scopeToParam(scope);
    if (param) window.localStorage.setItem(SCOPE_STORAGE_KEY, param);
    else window.localStorage.removeItem(SCOPE_STORAGE_KEY);
  } catch {
    // storage unavailable (private mode) — the URL still carries the scope.
  }
}

/** The two tabs: the open ones (default) and the past ones. A shared `?view=attention`
 *  link from the old board lands on the open list — nothing hidden. */
type BoardView = 'open' | 'past';

export function DisruptionsRoute() {
  const { t } = useTranslation('disruptions');
  const [searchParams, setSearchParams] = useSearchParams();
  const view: BoardView = searchParams.get('view') === 'past' ? 'past' : 'open';
  // URL wins (shareable); the stored default covers a fresh navigation.
  const scope = parseScope(searchParams.get('scope')) ?? readStoredScope() ?? NETWORK_SCOPE;

  const setParams = (next: { view?: BoardView; scope?: DisruptionsScope }) => {
    const out: Record<string, string> = {};
    const v = next.view ?? view;
    if (v === 'past') out.view = 'past';
    const scopeParam = scopeToParam(next.scope ?? scope);
    if (scopeParam) out.scope = scopeParam;
    if (next.scope) storeScope(next.scope);
    setSearchParams(out);
  };

  // The overview narrowed to the lens AND the header airport scope (NEX-310), grouped by
  // day, live, with the flights' routes — use-open-board.ts.
  const board = useOpenBoard({ enabled: view === 'open', scope });
  const { overview, allRows, headerAirports, groups, kpis, next } = board;

  const { data: operatedAirports } = useOperatedAirports(true);
  const airportOptions = useMemo(() => {
    const codes = new Set<string>((operatedAirports ?? []).map((o) => o.code));
    for (const row of allRows) for (const code of rowAirportCodes(row)) codes.add(code);
    return [...codes].sort();
  }, [operatedAirports, allRows]);
  const countryOptions = useMemo(() => knownCountries(airportOptions), [airportOptions]);

  const scopeCaption =
    scope.kind === 'network'
      ? t('board.scope.yourNetwork')
      : scope.kind === 'region'
        ? scope.country
        : scope.airport;

  const scrollRef = useRef<HTMLDivElement>(null);
  const empty = groups.today.length + groups.yesterday.length + groups.stale.length === 0;

  return (
    <main className="flex h-full flex-col overflow-hidden">
      <PageHeader
        title={t('board.title')}
        testId="disruptions-header"
        actions={
          <>
            <ScopeSwitcher
              t={t}
              scope={scope}
              countries={countryOptions}
              airports={airportOptions}
              onChange={(s) => setParams({ scope: s })}
            />
            {/* DECLARE → the ONE declare flow on /express, opened on its departures
                board (unified-declare §1) — the same door as the «Resumen» button. */}
            <Button asChild className="h-10 px-4 font-semibold">
              <Link to="/express" state={declareSourceState('BOARD')} data-testid="disruptions-declare">
                <Plus className="h-4 w-4" aria-hidden />
                {t('create.trigger')}
              </Link>
            </Button>
          </>
        }
      >
        <ViewTabs t={t} value={view} onChange={(v) => setParams({ view: v })} />
      </PageHeader>

      <div
        ref={scrollRef}
        className={cn(
          'density-page min-h-0 flex-1 overflow-y-auto p-4 lg:p-6',
          view === 'open' && 'lg:flex lg:flex-col lg:overflow-hidden',
        )}
        data-testid="disruptions-scroll"
      >
        <div className={cn('flex w-full flex-col gap-4', view === 'open' && 'lg:min-h-0 lg:flex-1')}>
          {view === 'past' ? (
            <PastPanel t={t} airports={airportsCsv(headerAirports)} scrollRef={scrollRef} />
          ) : overview.isError ? (
            <EmptyState title={t('board.error.title')} description={t('board.error.description')} />
          ) : overview.isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <>
              <BoardHero
                t={t}
                kpis={kpis}
                truncated={overview.data?.truncated ?? false}
                scopeCaption={scopeCaption}
                next={next}
                cases={next ? board.casesByUrn.get(next.row.urn) : undefined}
                pending={next ? board.pendingUrns.has(next.row.urn) : false}
              />
              {empty ? (
                <EmptyState
                  title={t('board.empty.title')}
                  description={t('board.empty.description')}
                />
              ) : (
                <>
                  <DayGroupSection
                    t={t}
                    group="today"
                    rows={groups.today}
                    casesByUrn={board.casesByUrn}
                    pendingUrns={board.pendingUrns}
                    nextUrn={next?.row.urn ?? null}
                  />
                  {groups.yesterday.length > 0 ? (
                    <DayGroupSection
                      t={t}
                      group="yesterday"
                      rows={groups.yesterday}
                      casesByUrn={board.casesByUrn}
                      pendingUrns={board.pendingUrns}
                      nextUrn={next?.row.urn ?? null}
                    />
                  ) : null}
                  {groups.stale.length > 0 ? (
                    <StaleSection
                      t={t}
                      rows={groups.stale}
                      pax={kpis.stale}
                      truncated={overview.data?.truncated ?? false}
                    />
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}

/* ── Nexa's card: the two numbers and «Lo que sigue» ─────────────────────────── */

/** A count as the board prints it: «12+» when it is a floor (the BFF's page walk hit
 *  its cap, or some rows do not know their count yet). */
function countText(n: number, floor: boolean): string {
  return `${n.toLocaleString()}${floor ? '+' : ''}`;
}

/**
 * The navy card on top — Operar's «Lo que sigue» card (operar/nexa-guide.tsx: the navy,
 * the Nexa mark, the cyan label, the ONE filled Nexa-blue button). Left: the disruption
 * that needs the operator most (`nextOnBoard`) in one sentence and the button that opens
 * it in Operar; with none, what the board is about. Right: the passengers still
 * unresolved, today and yesterday.
 */
function BoardHero({
  t,
  kpis,
  truncated,
  scopeCaption,
  next,
  cases,
  pending,
}: {
  t: TFunction<'disruptions'>;
  kpis: IBoardKpis;
  /** The BFF's page walk hit its cap — every count is a floor («12+»). */
  truncated: boolean;
  scopeCaption: string;
  next: IBoardNext | null;
  /** The next row's cached cases — its flight and route for the sentence. */
  cases: readonly DisruptionCaseRow[] | undefined;
  /** Its detail is being read: hold the name's place, as its row does (no title →
   *  flight-code jump). */
  pending: boolean;
}) {
  const { n, partial } = kpis.unresolved;
  const flight = next ? boardFlights(next.row, cases)[0] : undefined;
  const name = next ? (flight?.code ?? next.row.title) : '';
  const route =
    flight?.origin && flight.destination
      ? t('board.queue.route', { origin: flight.origin, destination: flight.destination })
      : '';
  const entry = next ? disruptionEntry(next.row.urn) : null;
  return (
    <section
      aria-label={t('board.next.region')}
      data-testid="board-hero"
      className="relative shrink-0 overflow-hidden rounded-2xl bg-nexa-navy p-5 text-white shadow-lg"
    >
      <NexaGlyph
        tone="light"
        className="pointer-events-none absolute -bottom-12 -right-8 h-56 w-60 opacity-10"
      />
      <div className="relative grid gap-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-extrabold uppercase tracking-widest text-nexa-cyan-soft">
            {next ? t('board.next.label') : t('board.next.nexa', { scope: scopeCaption })}
          </p>
          {next && entry ? (
            <>
              <p className="text-xl font-bold leading-snug sm:text-2xl" data-testid="board-next">
                {pending && !flight ? (
                  // a span, not <Skeleton> (a div): this sits inside a <p>
                  <span
                    aria-hidden
                    className="inline-block h-7 w-32 animate-pulse rounded-md bg-white/15 align-middle"
                  />
                ) : (
                  <span className="font-mono">{name}</span>
                )}
                {route ? <span className="ml-2 text-base font-medium text-nexa-mist">{route}</span> : null}
                {flight?.departureAt ? (
                  <FlightWhen
                    departureAt={flight.departureAt}
                    origin={flight.origin}
                    className="ml-3 text-base font-medium text-white"
                  />
                ) : null}
              </p>
              <p className="text-sm text-nexa-mist" data-testid="board-next-why">
                {next.reason === 'decision'
                  ? t('board.next.decision')
                  : t('board.next.unresolved', {
                      count: next.row.unresolvedPassengers,
                      n: next.row.unresolvedPassengers.toLocaleString(),
                    })}
              </p>
              <Button asChild className={cn(NEXA_FILLED, 'mt-1 gap-2 px-5')}>
                <Link to={entry.to} state={entry.state} data-testid="board-next-open">
                  {next.reason === 'decision'
                    ? t('board.queue.decide')
                    : flight
                      ? t('board.next.open', { name: flight.code })
                      : t('board.queue.operate')}
                  <ChevronRight aria-hidden className="h-4 w-4" />
                </Link>
              </Button>
            </>
          ) : (
            <p className="text-xl font-bold leading-snug sm:text-2xl" data-testid="board-next-none">
              {t('board.subtitle', { scope: scopeCaption })}
            </p>
          )}
        </div>
        <dl
          className="flex border-t border-white/15 pt-4 md:border-l md:border-t-0 md:pl-8 md:pt-0"
          data-testid="board-kpis"
        >
          <div data-testid="kpi-unresolved">
            {/* The scope («hoy y ayer») is part of the label, at the label's size: a 0 here
                never reads as «nobody needs you» while «Sin movimiento» holds passengers. */}
            <dt className="max-w-[12rem] text-xs font-semibold uppercase tracking-wide text-nexa-mist">
              {t('board.kpi.unresolved')}
            </dt>
            <dd
              className={cn(
                'mt-1 font-mono text-4xl font-bold leading-none tabular-nums',
                n !== null && n > 0 ? 'text-rose-300' : undefined,
              )}
              data-testid="kpi-unresolved-value"
            >
              {n === null ? t('board.queue.noData') : countText(n, truncated || partial)}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

/* ── Day groups ─────────────────────────────────────────────────────────────── */

const GROUP_VARIANT: Readonly<Record<Exclude<DayGroup, 'stale'>, QueueVariant>> = {
  today: 'main',
  yesterday: 'compact',
};

/** A day group: one bordered board — its name and count, the column header, the rows. */
function DayGroupSection({
  t,
  group,
  rows,
  casesByUrn,
  pendingUrns,
  nextUrn,
}: {
  t: TFunction<'disruptions'>;
  group: Exclude<DayGroup, 'stale'>;
  rows: DisruptionOverviewRow[];
  casesByUrn: ReadonlyMap<string, readonly DisruptionCaseRow[]>;
  pendingUrns: ReadonlySet<string>;
  nextUrn: string | null;
}) {
  const headingId = useId();
  const main = group === 'today';
  return (
    <section
      aria-labelledby={headingId}
      data-testid={`group-${group}`}
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card lg:flex lg:flex-col',
        // the desktop fit (see the top of this file): a group keeps its header and one
        // row; «Ayer» gives its height up first. Flex shrinks in proportion to
        // shrink × size, so against «Ayer»'s 10000 «Hoy» (1) loses nothing until
        // «Ayer» sits at its minimum — a mere 100 still cut a few pixels off a «Hoy»
        // that fits (a scrollbar for nothing). «Hoy» stays at 1, not below: once
        // «Ayer» is frozen, factors summing under 1 hand out only that fraction of
        // the overflow and the page scrolls again.
        rows.length === 0
          ? 'lg:shrink-0'
          : main
            ? 'lg:min-h-[10rem]'
            : 'lg:min-h-[9rem] lg:shrink-[10000]',
      )}
    >
      <h2
        id={headingId}
        className={cn(
          'flex shrink-0 items-center gap-2 px-4 py-3 font-semibold',
          main ? 'text-lg' : 'text-base text-muted-foreground',
        )}
      >
        {t(`board.groups.${group}`)}
        <CountChip n={rows.length} testId={`group-${group}-count`} />
      </h2>
      {rows.length === 0 ? (
        <p className="mx-4 mb-4 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          {t('board.groups.todayEmpty')}
        </p>
      ) : (
        <>
          <BoardColumns />
          <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            <PriorityQueue
              rows={rows}
              variant={GROUP_VARIANT[group]}
              casesByUrn={casesByUrn}
              pendingUrns={pendingUrns}
              nextUrn={nextUrn}
              testId={`queue-${group}`}
            />
          </div>
        </>
      )}
    </section>
  );
}

/** «Sin movimiento» — collapsed by default: its count, the passengers it still holds
 *  unresolved (so a board of zombies never reads as zero) and one line of why. */
function StaleSection({
  t,
  rows,
  pax,
  truncated,
}: {
  t: TFunction<'disruptions'>;
  rows: DisruptionOverviewRow[];
  pax: IPaxSum;
  truncated: boolean;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  return (
    <section
      data-testid="group-stale"
      className={cn(
        'overflow-hidden rounded-lg border border-dashed border-border bg-muted/30',
        // closed it is one line that never gives way; open, its list scrolls inside like «Ayer»
        open ? 'lg:flex lg:min-h-[9rem] lg:flex-col lg:shrink-[10000]' : 'lg:shrink-0',
      )}
    >
      <h2 className="shrink-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((o) => !o)}
          data-testid="group-stale-toggle"
          className="flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left text-muted-foreground transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <ChevronDown
            aria-hidden
            className={cn('h-4 w-4 shrink-0 transition-transform', open ? 'rotate-0' : '-rotate-90')}
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2 text-base font-semibold">
              {t('board.groups.stale')}
              <CountChip n={rows.length} testId="group-stale-count" />
              {pax.n !== null && pax.n > 0 ? (
                <span
                  className="text-sm font-semibold text-foreground"
                  data-testid="group-stale-unresolved"
                >
                  {t('board.groups.staleUnresolved', {
                    count: pax.n,
                    n: countText(pax.n, truncated || pax.partial),
                  })}
                </span>
              ) : null}
            </span>
            <span className="block text-xs font-normal">{t('board.groups.staleWhy')}</span>
          </span>
        </button>
      </h2>
      {/* `lg:flex` only while open — a display class would override the `hidden` attribute */}
      <div id={listId} hidden={!open} className={cn(open && 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col')}>
        {open ? (
          <>
            <BoardColumns />
            <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
              <PriorityQueue rows={rows} variant="quiet" testId="queue-stale" />
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}

/* ── Scope switcher (Network / Region / Airport) ──────────────────────────── */

/** The header's segmented controls — the lens and the two tabs: the console's button
 *  shape (`rounded-md`, never a pill — PO 2026-10-29), the chosen one tinted in the
 *  brand like the /express filter chips. */
const SEGMENTED = 'inline-flex items-center gap-0.5 rounded-md border border-border bg-background p-[3px]';
const SEGMENT =
  'h-8 rounded-sm px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const SEGMENT_ON = 'bg-primary/10 font-semibold text-primary';
const SEGMENT_OFF = 'font-medium text-muted-foreground hover:bg-muted hover:text-foreground';
const HEADER_SELECT = 'h-10 rounded-md border border-border bg-background px-3 text-sm';

function ScopeSwitcher({
  t,
  scope,
  countries,
  airports,
  onChange,
}: {
  t: TFunction<'disruptions'>;
  scope: DisruptionsScope;
  countries: string[];
  airports: string[];
  onChange: (scope: DisruptionsScope) => void;
}) {
  const kinds: { kind: DisruptionsScope['kind']; label: string }[] = [
    { kind: 'network', label: t('board.scope.network') },
    { kind: 'region', label: t('board.scope.region') },
    { kind: 'airport', label: t('board.scope.airport') },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="scope-switcher">
      <div className={SEGMENTED} role="tablist" aria-label={t('board.scope.label')}>
        {kinds.map(({ kind, label }) => (
          <button
            key={kind}
            type="button"
            role="tab"
            aria-selected={scope.kind === kind}
            data-testid={`scope-${kind}`}
            onClick={() => {
              if (kind === scope.kind) return;
              if (kind === 'network') onChange(NETWORK_SCOPE);
              else if (kind === 'region') onChange({ kind: 'region', country: countries[0] ?? 'Argentina' });
              else onChange({ kind: 'airport', airport: airports[0] ?? 'AEP' });
            }}
            className={cn(SEGMENT, scope.kind === kind ? SEGMENT_ON : SEGMENT_OFF)}
          >
            {label}
          </button>
        ))}
      </div>
      {scope.kind === 'region' ? (
        <select
          aria-label={t('board.scope.regionSelect')}
          className={HEADER_SELECT}
          value={scope.country}
          onChange={(e) => onChange({ kind: 'region', country: e.target.value })}
        >
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      ) : null}
      {scope.kind === 'airport' ? (
        <select
          aria-label={t('board.scope.airportSelect')}
          className={HEADER_SELECT}
          value={scope.airport}
          onChange={(e) => onChange({ kind: 'airport', airport: e.target.value })}
        >
          {airports.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

/* ── Past panel (terminal disruptions — the classic paginated list) ───────── */

function PastPanel({
  t,
  airports,
  scrollRef,
}: {
  t: TFunction<'disruptions'>;
  airports?: string | undefined;
  /** The board's scroller — the sentinel's root (NEX-604). */
  scrollRef: RefObject<HTMLDivElement | null>;
}) {
  const { items, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useDisruptionsList({ view: 'past', ...(airports ? { airports } : {}) });
  const rows = useMemo<DisruptionOverviewRow[]>(
    () =>
      items.map((row) => ({
        ...row,
        severity: 'settled',
        unresolvedPassengers: Math.max(
          0,
          (row.rollup.affectedPassengers ?? 0) - (row.rollup.rebookedPassengers ?? 0),
        ),
      })),
    [items],
  );
  if (isError) {
    return <EmptyState title={t('board.error.title')} description={t('board.error.description')} />;
  }
  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        title={t('board.past.emptyTitle')}
        description={t('board.past.emptyDescription')}
      />
    );
  }
  return (
    <section
      className="overflow-hidden rounded-lg border border-border bg-card"
      aria-label={t('board.views.past')}
      data-testid="group-past"
    >
      <BoardColumns />
      <PriorityQueue rows={rows} variant="compact" />
      {isFetchingNextPage ? (
        <p className="p-3 text-center text-xs text-muted-foreground">
          {t('board.past.loadingMore')}
        </p>
      ) : null}
      <InfiniteScrollSentinel
        onReachEnd={fetchNextPage}
        disabled={!hasNextPage || isFetchingNextPage}
        scrollContainerRef={scrollRef}
      />
    </section>
  );
}

/* ── View tabs ──────────────────────────────────────────────────────── */

const VIEWS: readonly BoardView[] = ['open', 'past'];

function ViewTabs({
  t,
  value,
  onChange,
}: {
  t: TFunction<'disruptions'>;
  value: BoardView;
  onChange: (view: BoardView) => void;
}) {
  return (
    <div className={SEGMENTED} role="tablist" aria-label={t('board.views.label')}>
      {VIEWS.map((v) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={value === v}
          onClick={() => onChange(v)}
          data-testid={`disruptions-view-${v}`}
          className={cn(SEGMENT, value === v ? SEGMENT_ON : SEGMENT_OFF)}
        >
          {t(`board.views.${v}`)}
        </button>
      ))}
    </div>
  );
}
